import React, { useState } from 'react';

import {
	MoonStar,
	ChevronsUpDown,
	Monitor,
	Sun,
	SunMoon,
	LogOut,
	Settings,
	Sparkles,
	HelpingHand,
	Bell,
	BellDot,
	Eye,
	EyeOff,
	ScrollText,
} from 'lucide-react';
import store from 'store';

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import ReferralDialog from '@/components/referral/ReferralDialog';

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem as BaseDropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar';
import UserAvatar from '../webchat/teams/UserAvatar';
import { handleDisableNotificationsOnLogout } from '@/actions/browserNotifications';
import { clearRecentConversations } from '@/utils/recentConversations';
import { useDispatch } from 'react-redux';
import { useTheme } from '@/components/ui/theme-provider';
import { useNavigate } from 'react-router-dom';
import LayoutSwitcher from './LayoutSwitcher';
import { useSelector } from 'react-redux';
import { useAnnouncements } from '@/components/announcements/useAnnouncements';
import { AnnouncementList } from '@/components/announcements/AnnouncementList';
import { ActionSheet, type ActionSheetSection } from '@/components/mobile';
import { canSwitchLayout, isPhoneDevice } from './layout-preference';
import {
	isBubbleHiddenByUser,
	setBubbleHiddenByUser,
	syncLauncherVisibility,
} from './support';
import { focusOpener, returnFocusAfterClose } from './focus-return';

const THEME_OPTIONS = [
	{ value: 'light', label: 'Light', icon: Sun },
	{ value: 'dark', label: 'Dark', icon: MoonStar },
	{ value: 'system', label: 'System', icon: Monitor },
] as const;

type OpenReferral = (open: boolean, opener?: HTMLElement | null) => void;
const ReferralDialogContext = React.createContext<OpenReferral | null>(null);

/**
 * Keeps Refer us, and what has been typed into it, open above the sidebar,
 * which swaps between the phone drawer and the desktop sidebar (and remounts
 * NavUser) when a phone rotates across the md breakpoint.
 */
export function ReferralDialogProvider({ children }: { children: React.ReactNode }) {
	const { isMobile } = useSidebar();
	const [open, setOpen] = useState(false);
	const opener = React.useRef<HTMLElement | null>(null);
	const openReferral = React.useCallback<OpenReferral>((value, from) => {
		if (value) opener.current = from ?? null;
		setOpen(value);
	}, []);
	// Phones open it from the account sheet, so Radix has no trigger to hand
	// focus back to when it closes.
	React.useEffect(() => {
		if (open || !isMobile || !opener.current) return;
		const target = opener.current;
		opener.current = null;
		return returnFocusAfterClose(target);
	}, [open, isMobile]);
	return (
		<ReferralDialogContext.Provider value={openReferral}>
			{children}
			<Dialog open={open} onOpenChange={(value) => openReferral(value)}>
				<ReferralDialog />
			</Dialog>
		</ReferralDialogContext.Provider>
	);
}

export function NavUser({
	user,
	addon,
}: {
	addon?: React.ReactNode;
	user: {
		name: string;
		username: string;
		avatar: string;
	};
}) {
	const { isMobile } = useSidebar();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { setTheme, theme } = useTheme();

	const auth = useSelector((state: any) => state.auth);
	const { announcements, hasUnread, markAsRead } = useAnnouncements();
	const [announcementsOpen, setAnnouncementsOpen] = useState(false);
	// Phones hand focus back here when Announcements or Refer us closes (a
	// tapped menu trigger doesn't take focus, so document.activeElement won't do).
	const accountButton = React.useRef<HTMLButtonElement>(null);
	const sharedReferral = React.useContext(ReferralDialogContext);
	const [referralOpen, setLocalReferralOpen] = useState(false);
	const setReferralOpen: OpenReferral =
		sharedReferral ?? ((open) => setLocalReferralOpen(open));
	// Desktop opens the dropdown; phones get an action sheet from the same button.
	const [menuOpen, setMenuOpen] = useState(false);

	const [bubbleHidden, setBubbleHidden] = React.useState(isBubbleHiddenByUser);
	const phoneDevice = isPhoneDevice();

	React.useEffect(() => {
		setBubbleHiddenByUser(bubbleHidden);
		// Phones keep the bubble hidden whatever the preference is.
		syncLauncherVisibility();
	}, [bubbleHidden]);

	const handleLogout = async () => {
		// stop this browser's desktop notifications before the session ends
		await dispatch(handleDisableNotificationsOnLogout() as any);
		clearRecentConversations();
		store.remove('jwt');
		document.location = `https://${window.location.hostname}/api/logout`;
	};

	const openAnnouncements = () => {
		markAsRead();
		setAnnouncementsOpen(true);
	};

	// Settings, Walkthroughs and Help have their own rows in the phone drawer.
	const accountSections: ActionSheetSection[] = [
		...(!auth.reseller
			? [
					{
						items: [
							{
								label: 'Announcements',
								icon: hasUnread ? BellDot : Bell,
								trailing: hasUnread ? (
									<span className="block size-2.5 rounded-full bg-destructive">
										<span className="sr-only">New</span>
									</span>
								) : undefined,
								onSelect: openAnnouncements,
								afterClose: true,
							},
							{
								label: 'Refer us',
								description: 'Get a free month',
								icon: Sparkles,
								onSelect: () => setReferralOpen(true, accountButton.current),
								afterClose: true,
							},
						],
					},
				]
			: []),
		{
			items: [
				{
					label: 'Appearance',
					icon: SunMoon,
					trailing: THEME_OPTIONS.find((option) => option.value === theme)?.label,
					page: 'theme',
				},
				{
					label: 'Terms & policies',
					icon: ScrollText,
					onSelect: () => navigate('/legal'),
				},
			],
		},
		{
			items: [
				{
					label: 'Log out',
					icon: LogOut,
					destructive: true,
					onSelect: handleLogout,
				},
			],
		},
	];

	return (
		<>
			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu
						modal={false}
						open={menuOpen && !isMobile}
						onOpenChange={setMenuOpen}
					>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton
								ref={accountButton}
								data-onboarding="user-menu"
								size="lg"
								aria-label={`Account: ${user.name || user.username}`}
								className="relative data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
							>
								<div className="relative">
									<UserAvatar
										user={user}
										showName={false}
										showNameBelow={false}
										avatarHeight="30px"
										avatarWidth="30px"
										avatarBgColor={false}
										avatarFontSize=""
										marginLeft={0}
										marginTop={0}
										nameFontSize={0}
									/>
									{hasUnread && (
										<span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-sidebar" />
									)}
								</div>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-semibold">
										{user.name}
									</span>
									<span className="truncate text-xs">
										{user.username}
									</span>
								</div>
								<ChevronsUpDown className="ml-auto size-4" />
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
							side={isMobile ? 'bottom' : 'right'}
							align="end"
							sideOffset={4}
							onInteractOutside={(event) => {
								if (event.target instanceof Element && event.target.closest('[data-onboarding-guide]')) event.preventDefault();
							}}
							onFocusOutside={(event) => {
								if (event.target instanceof Element && event.target.closest('[data-onboarding-guide]')) event.preventDefault();
							}}
						>
							<DropdownMenuLabel className="p-0 font-normal">
								<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
									<UserAvatar
										user={user}
										showName={false}
										showNameBelow={false}
										avatarHeight="30px"
										avatarWidth="30px"
										avatarBgColor={false}
										avatarFontSize=""
										marginLeft={0}
										marginTop={0}
										nameFontSize={0}
									/>
									<div className="grid flex-1 text-left text-sm leading-tight">
										<span className="truncate font-semibold">
											{user.name}
										</span>
										<span className="truncate text-xs">
											{user.username}
										</span>
									</div>
								</div>
							</DropdownMenuLabel>
							{!auth.reseller && (
								<button
									className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-default"
									onClick={() => {
										markAsRead();
										setAnnouncementsOpen(true);
									}}
								>
									{hasUnread ? (
										<BellDot className="size-4 text-destructive" />
									) : (
										<Bell className="size-4" />
									)}
									<span>Announcements</span>
									{hasUnread && (
										<span className="ml-auto h-2 w-2 rounded-full bg-destructive" />
									)}
								</button>
							)}
							<DropdownMenuSeparator />

<DropdownMenuGroup>
								{!auth.reseller && (
									<>
										<DropdownMenuItem
											aria-haspopup="dialog"
											onClick={() => setReferralOpen(true)}
										>
											<Sparkles className="size-4" />
											<div className="flex flex-col">
												Refer us
												<p className="text-xs text-muted-foreground">
													get a free month
												</p>
											</div>
										</DropdownMenuItem>
									</>
								)}
							</DropdownMenuGroup>

							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								{addon}
								<div className="flex items-center gap-0">
									{!auth.reseller && (
										<DropdownMenuItem
											className={phoneDevice ? 'flex-1' : 'flex-1 rounded-r-none'}
											onClick={() =>
												typeof window.Intercom ===
													'function' &&
												window.Intercom('show')
											}
										>
											<HelpingHand className="size-4" />
											Get support
										</DropdownMenuItem>
									)}
									{/* Phones (here in landscape) never show the bubble. */}
									{!phoneDevice && (
										<DropdownMenuItem
											className="rounded-l-none text-xs text-muted-foreground"
											onSelect={(e) => e.preventDefault()}
											onClick={() =>
												setBubbleHidden((prev) => !prev)
											}
										>
											{bubbleHidden ? (
												<>
													<Eye className="size-4" />
													Show bubble
												</>
											) : (
												<>
													<EyeOff className="size-4" />
													Hide bubble
												</>
											)}
										</DropdownMenuItem>
									)}
								</div>
								<DropdownMenuItem
									data-onboarding="user-settings"
									onClick={() => navigate('/settings')}
								>
									<Settings className="size-4" />
									Settings
								</DropdownMenuItem>

								<DropdownMenuSub>
									<DropdownMenuSubTrigger className="flex items-center gap-2 px-1 py-1.5">
										<SunMoon className="size-4" />
										<span>Theme</span>
									</DropdownMenuSubTrigger>
									<DropdownMenuSubContent>
										<DropdownMenuRadioGroup
											value={theme}
											onValueChange={setTheme}
										>
											<DropdownMenuRadioItem value="light">
												<Sun className="size-4 mr-2" />
												Light
											</DropdownMenuRadioItem>
											<DropdownMenuRadioItem value="dark">
												<MoonStar className="size-4 mr-2" />
												Dark
											</DropdownMenuRadioItem>
											<DropdownMenuRadioItem value="system">
												<Monitor className="size-4 mr-2" />
												System
											</DropdownMenuRadioItem>
										</DropdownMenuRadioGroup>
									</DropdownMenuSubContent>
								</DropdownMenuSub>
								<DropdownMenuItem
									onClick={() => navigate('/legal')}
								>
									<ScrollText className="size-4" />
									Terms &amp; policies
								</DropdownMenuItem>
								{canSwitchLayout() && (
									<DropdownMenuItem>
										<LayoutSwitcher />
									</DropdownMenuItem>
								)}
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={handleLogout}>
								<LogOut className="size-4" />
								Log out
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
			{!sharedReferral && (
				<Dialog open={referralOpen} onOpenChange={setLocalReferralOpen}>
					<ReferralDialog />
				</Dialog>
			)}
			{isMobile && (
				<ActionSheet
					open={menuOpen}
					onOpenChange={setMenuOpen}
					title={user.username || user.name}
					sections={accountSections}
					pages={[
						{
							id: 'theme',
							title: 'Appearance',
							sections: [
								{
									items: THEME_OPTIONS.map((option) => ({
										label: option.label,
										icon: option.icon,
										checked: theme === option.value,
										onSelect: () => setTheme(option.value),
									})),
								},
							],
						},
					]}
				/>
			)}
			<Dialog
				open={announcementsOpen}
				onOpenChange={setAnnouncementsOpen}
			>
				<DialogContent
					className="sm:max-w-[600px] max-h-[80vh] flex flex-col"
					onCloseAutoFocus={(event) => {
						if (isMobile && focusOpener(accountButton.current)) event.preventDefault();
					}}
				>
					<DialogHeader>
						<DialogTitle>Announcements</DialogTitle>
					</DialogHeader>
					<div className="overflow-auto -mx-6 px-6 pb-1">
						<AnnouncementList announcements={announcements} />
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}

export const DropdownMenuItem = ({
	children,
	className,
	...props
}: React.ComponentProps<typeof BaseDropdownMenuItem>) => {
	return (
		<BaseDropdownMenuItem
			className={`flex items-center gap-2 px-1 py-1.5 text-left ${className}`}
			{...props}
		>
			{children}
		</BaseDropdownMenuItem>
	);
};
